// ADR-012 i18n — "ops" cluster label catalog. Spread into the central LABELS
// in labels.ts. EN values MUST equal the screen's current hardcoded English so
// existing text-based tests stay green; HE values are the Hebrew translation.
export const OPS_LABELS = {
  // ═══════════════════════════════════════════════════════════════════════
  //  Configuration — list (ConfigListPage)
  // ═══════════════════════════════════════════════════════════════════════
  configListTitle: { en: 'Configuration', he: 'תצורה' },
  configListNewEntry: { en: '+ New Entry', he: '+ רשומה חדשה' },
  configListColKey: { en: 'Key', he: 'מפתח' },
  configListColValue: { en: 'Value', he: 'ערך' },
  configListColScope: { en: 'Scope', he: 'תחום' },
  configListColService: { en: 'Service', he: 'שירות' },
  configListColUpdated: { en: 'Updated', he: 'עודכן' },
  // OP-577 — editing a live config value from the portal. The API and configApi.setValue both
  // already existed; only the UI was missing, so a platformAdmin could see a live switch and
  // not change it.
  configListColActions: { en: 'Actions', he: 'פעולות' },
  configListEditScopeLine: {
    en: 'Scope: {scope} · Service: {service} — a value edited at the wrong scope looks correct here and is ignored at runtime.',
    he: 'היקף: {scope} · שירות: {service} — ערך שנערך בהיקף שגוי נראה תקין כאן ומתעלמים ממנו בזמן ריצה.',
  },
  configListConfirmTitle: { en: 'Change this live value?', he: 'לשנות את הערך בזמן אמת?' },
  configListConfirmBody: {
    en: 'Change {key} from "{from}" to "{to}"? This is a production change: it takes effect without a deploy, a review or a test, and there is no automatic rollback.',
    he: 'לשנות את {key} מ-"{from}" ל-"{to}"? זהו שינוי בסביבת ייצור: הוא נכנס לתוקף ללא פריסה, ללא בדיקה וללא סקירה, ואין חזרה אוטומטית לאחור.',
  },
  configListArmingWarning: {
    en: 'This key decides whether the system acts toward real people on its own — sending email, messaging, or writing to an external system. Enumerate exactly which tenants and mailboxes it arms, by address, before changing it. See the Autonomous Action Safety Protocol.',
    he: 'מפתח זה קובע אם המערכת פועלת מול אנשים אמיתיים באופן עצמאי — שליחת דוא"ל, הודעות, או כתיבה למערכת חיצונית. לפני שינוי, פרטו בדיוק אילו דיירים ותיבות דואר הוא מפעיל, לפי כתובת. ראו את פרוטוקול הבטיחות לפעולות אוטונומיות.',
  },
  configListSaveError: { en: 'Could not save the value', he: 'שמירת הערך נכשלה' },
  configListLoadError: { en: 'Failed to load configuration entries.', he: 'טעינת רשומות התצורה נכשלה.' },
  configListLoading: { en: 'Loading configuration...', he: 'טוען תצורה...' },
  configListBanner: {
    en: 'Advanced platform settings — for technical support/ops use only.',
    he: 'הגדרות פלטפורמה מתקדמות — לשימוש צוות תמיכה/תפעול טכני בלבד.',
  },

  // ═══════════════════════════════════════════════════════════════════════
  //  Configuration — form (ConfigFormPage)
  // ═══════════════════════════════════════════════════════════════════════
  configFormTitle: { en: 'New Configuration Entry', he: 'רשומת תצורה חדשה' },
  configFormKeyLabel: { en: 'Key', he: 'מפתח' },
  configFormKeyPlaceholder: { en: 'Feature:MaxRetries', he: 'Feature:MaxRetries' },
  configFormValueLabel: { en: 'Value', he: 'ערך' },
  configFormScopeLabel: { en: 'Scope', he: 'תחום' },
  configFormScopeGlobal: { en: 'Everyone', he: 'כולם' },
  configFormScopeService: { en: 'One service', he: 'שירות אחד' },
  configFormScopeTenant: { en: 'One organization', he: 'ארגון אחד' },
  configFormScopeServiceTenant: { en: 'One service, one organization', he: 'שירות אחד, ארגון אחד' },
  configFormServiceLabel: { en: 'Service Name', he: 'שם שירות' },
  configFormServicePlaceholder: { en: 'Required for service / service+tenant scope', he: 'נדרש עבור תחום שירות / שירות+ארגון' },
  configFormTenantLabel: { en: 'Tenant ID', he: 'מזהה ארגון' },
  configFormTenantPlaceholder: { en: 'Required for tenant / service+tenant scope', he: 'נדרש עבור תחום ארגון / שירות+ארגון' },
  configFormCreating: { en: 'Creating...', he: 'יוצר...' },
  configFormCreate: { en: 'Create Entry', he: 'צור רשומה' },
  configFormCreateError: { en: 'Failed to create configuration entry.', he: 'יצירת רשומת התצורה נכשלה.' },

  // ═══════════════════════════════════════════════════════════════════════
  //  Templates — shared status / channel labels
  // ═══════════════════════════════════════════════════════════════════════
  templateStatusDraft: { en: 'Draft', he: 'טיוטה' },
  templateStatusSubmitted: { en: 'Waiting for approval', he: 'ממתין לאישור' },
  templateStatusApproved: { en: 'Approved', he: 'מאושר' },
  templateStatusRejected: { en: 'Rejected', he: 'נדחה' },
  templateStatusArchived: { en: 'Archived', he: 'בארכיון' },
  templateChannelWebchat: { en: 'Web chat', he: 'צ׳אט באתר' },
  templateChannelWhatsapp: { en: 'WhatsApp', he: 'וואטסאפ' },
  templateChannelMessenger: { en: 'Messenger', he: 'מסנג׳ר' },
  templateChannelEmail: { en: 'Email', he: 'אימייל' },

  // ═══════════════════════════════════════════════════════════════════════
  //  Templates — list (TemplateListPage)
  // ═══════════════════════════════════════════════════════════════════════
  templateListLoading: { en: 'Loading templates...', he: 'טוען תבניות...' },
  templateListTitle: { en: 'Templates', he: 'תבניות' },
  templateListNew: { en: '+ New template', he: '+ תבנית חדשה' },
  templateListColName: { en: 'Name', he: 'שם' },
  templateListColChannel: { en: 'Channel', he: 'ערוץ' },
  templateListColLanguage: { en: 'Language', he: 'שפה' },
  templateListColStatus: { en: 'Status', he: 'סטטוס' },
  templateListColUpdated: { en: 'Updated', he: 'עודכן' },
  templateListColActions: { en: 'Actions', he: 'פעולות' },
  templateListEmpty: { en: 'No templates yet.', he: 'אין תבניות עדיין.' },
  templateListSubmit: { en: 'Submit for approval', he: 'שלח לאישור' },
  templateListApprove: { en: 'Approve', he: 'אשר' },
  templateListReject: { en: 'Reject', he: 'דחה' },
  templateListView: { en: 'View', he: 'צפייה' },
  templateListLoadError: { en: 'Failed to load templates.', he: 'טעינת התבניות נכשלה.' },
  templateListSubmitConfirm: { en: 'Submit "{name}" for approval?', he: 'לשלוח את "{name}" לאישור?' },
  templateListSubmitError: { en: 'Failed to submit template for approval.', he: 'שליחת התבנית לאישור נכשלה.' },
  templateListApproveError: { en: 'Failed to approve template.', he: 'אישור התבנית נכשל.' },
  templateListRejectError: { en: 'Failed to reject template.', he: 'דחיית התבנית נכשלה.' },
  templateListDeleteConfirm: { en: 'Delete "{name}"? This cannot be undone.', he: 'למחוק את "{name}"? לא ניתן לבטל פעולה זו.' },
  templateListDeleteError: { en: 'Failed to delete template.', he: 'מחיקת התבנית נכשלה.' },
  templateListRejectAria: { en: 'Reject template', he: 'דחיית תבנית' },
  templateListRejectTitle: { en: 'Reject “{name}”', he: 'דחיית “{name}”' },
  templateListRejectPrompt: {
    en: 'Tell the author why this template was rejected. A reason is required.',
    he: 'ציין למחבר מדוע התבנית נדחתה. חובה לציין סיבה.',
  },
  templateListReasonLabel: { en: 'Reason', he: 'סיבה' },
  templateListReasonPlaceholder: { en: 'e.g., Off-brand wording in the greeting.', he: 'לדוגמה: ניסוח שאינו תואם למותג בברכה.' },
  templateListRejectConfirm: { en: 'Reject template', he: 'דחה תבנית' },

  // ═══════════════════════════════════════════════════════════════════════
  //  Templates — form (TemplateFormPage)
  // ═══════════════════════════════════════════════════════════════════════
  templateFormLoading: { en: 'Loading template...', he: 'טוען תבנית...' },
  templateFormTitleView: { en: 'View template', he: 'צפייה בתבנית' },
  templateFormTitleEdit: { en: 'Edit template', he: 'עריכת תבנית' },
  templateFormTitleNew: { en: 'New template', he: 'תבנית חדשה' },
  templateFormReadOnlyNotice: {
    en: 'This template is {state} and can no longer be changed.',
    he: 'תבנית זו {state} ולא ניתן עוד לשנות אותה.',
  },
  templateFormReadOnlyApproved: { en: 'approved', he: 'מאושרת' },
  templateFormReadOnlyInReview: { en: 'in review', he: 'בבדיקה' },
  templateFormValNameLen: { en: 'Name must be at least 3 characters.', he: 'השם חייב להכיל לפחות 3 תווים.' },
  templateFormValBodyEmpty: { en: 'The message body cannot be empty.', he: 'גוף ההודעה אינו יכול להיות ריק.' },
  templateFormValLangReq: { en: 'Language is required.', he: 'חובה לציין שפה.' },
  templateFormNameLabel: { en: 'Name *', he: 'שם *' },
  templateFormNamePlaceholder: { en: 'e.g., Order shipped notification', he: 'לדוגמה: הודעה על משלוח הזמנה' },
  templateFormNameHint: {
    en: 'A short, human-readable name to find this template later.',
    he: 'שם קצר וקריא לאיתור התבנית בהמשך.',
  },
  templateFormChannelLabel: { en: 'Channel *', he: 'ערוץ *' },
  templateFormLanguageLabel: { en: 'Language *', he: 'שפה *' },
  templateFormLanguagePlaceholder: { en: 'en', he: 'en' },
  templateFormLanguageHint: { en: 'A language code such as “en” or “he”.', he: 'קוד שפה כגון “en” או “he”.' },
  templateFormBodyLabel: { en: 'Message body *', he: 'גוף ההודעה *' },
  templateFormBodyPlaceholder: { en: 'Hi {{name}}, your order has shipped!', he: 'שלום {{name}}, ההזמנה שלך נשלחה!' },
  templateFormBodyHint: {
    en: 'Use {{variable}} placeholders for values filled in at send time.',
    he: 'השתמש ב-{{variable}} כמצייני מיקום לערכים שימולאו בעת השליחה.',
  },
  templateFormDescLabel: { en: 'Description', he: 'תיאור' },
  templateFormDescPlaceholder: { en: 'Optional — what this template is for.', he: 'רשות — למה משמשת התבנית.' },
  templateFormSaving: { en: 'Saving...', he: 'שומר...' },
  templateFormSaveDraft: { en: 'Save draft', he: 'שמור טיוטה' },
  templateFormBackToTemplates: { en: 'Back to templates', he: 'חזרה לתבניות' },
  templateFormLoadError: { en: 'Failed to load the template.', he: 'טעינת התבנית נכשלה.' },
  templateFormSaveError: { en: 'Failed to save the template.', he: 'שמירת התבנית נכשלה.' },

  // ═══════════════════════════════════════════════════════════════════════
  //  Audit log (AuditLogPage)
  // ═══════════════════════════════════════════════════════════════════════
  auditColTime: { en: 'Time', he: 'זמן' },
  auditColSeverity: { en: 'Severity', he: 'חומרה' },
  auditColLogType: { en: 'Log Type', he: 'סוג רשומה' },
  auditColAction: { en: 'Action', he: 'פעולה' },
  auditColService: { en: 'Service', he: 'שירות' },
  auditColTenant: { en: 'Tenant', he: 'ארגון' },
  auditColDetails: { en: 'Details', he: 'פרטים' },
  auditColTransaction: { en: 'Reference', he: 'אסמכתא' },
  auditTitle: { en: 'Audit Log', he: 'יומן ביקורת' },
  auditSearchPlaceholder: {
    en: 'Words or phrases — e.g. login, error, settings change',
    he: 'מילים או ביטויים — לדוגמה: כניסה, שגיאה, שינוי הגדרות',
  },
  auditTransactionIdLabel: { en: 'Reference ID', he: 'מזהה אסמכתא' },
  auditTransactionIdPlaceholder: {
    en: 'Paste the reference ID from a support ticket or error message',
    he: 'הדבק את מזהה האסמכתא מכרטיס תמיכה או מהודעת שגיאה',
  },
  auditSessionIdLabel: { en: 'Session ID', he: 'מזהה שיחה' },
  auditSessionIdPlaceholder: {
    en: 'Paste the session ID from the contact card in the Agent Console',
    he: 'הדבק את מזהה השיחה מכרטיס איש הקשר בקונסולת הנציגים',
  },
  auditTenantLabel: { en: 'Tenant', he: 'ארגון' },
  auditTenantAll: { en: 'All tenants', he: 'כל הארגונים' },
  auditTenantSystem: { en: 'Platform (system)', he: 'פלטפורמה (מערכת)' },
  auditSeverityLabel: { en: 'Severity', he: 'חומרה' },
  auditFilterAll: { en: 'All', he: 'הכל' },
  auditSeverityDebug: { en: 'Diagnostic', he: 'אבחון' },
  auditSeverityInfo: { en: 'Info', he: 'מידע' },
  auditSeverityMilestone: { en: 'Key event', he: 'אירוע מרכזי' },
  auditSeverityWarning: { en: 'Warning', he: 'אזהרה' },
  auditSeverityFatal: { en: 'Critical error', he: 'שגיאה קריטית' },
  auditLogTypeLabel: { en: 'Log Type', he: 'סוג רשומה' },
  auditLogTypeSecurity: { en: 'Security', he: 'אבטחה' },
  auditLogTypeOperational: { en: 'Operational', he: 'תפעולי' },
  auditLogTypeBusiness: { en: 'Business', he: 'עסקי' },
  auditStartDateLabel: { en: 'Start Date', he: 'תאריך התחלה' },
  auditEndDateLabel: { en: 'End Date', he: 'תאריך סיום' },
  auditLoadError: { en: 'Failed to load audit logs.', he: 'טעינת יומני הביקורת נכשלה.' },
  auditAllTenantDateError: {
    en: 'Choose a start and end date for an all-tenant search.',
    he: 'בחר תאריך התחלה וסיום עבור חיפוש בכל הארגונים.',
  },
  auditLoading: { en: 'Loading audit logs...', he: 'טוען יומני ביקורת...' },

  // ═══════════════════════════════════════════════════════════════════════
  //  Ticket imports (TicketImportsPage)
  // ═══════════════════════════════════════════════════════════════════════
  ticketImportsTitle: { en: 'Import historical tickets', he: 'ייבוא כרטיסים היסטוריים' },
  ticketImportsAgentLabel: { en: 'Agent:', he: 'סוכן:' },
  ticketImportsBackToAgent: { en: 'Back to agent', he: 'חזרה לסוכן' },
  ticketImportsEditMapping: { en: 'Edit mapping', he: 'עריכת מיפוי' },
  ticketImportsLoadAgentError: { en: 'Failed to load agent.', he: 'טעינת הסוכן נכשלה.' },
  ticketImportsLoadError: { en: 'Failed to load ticket imports.', he: 'טעינת ייבוא הכרטיסים נכשלה.' },
  ticketImportsPollGiveUp: { en: 'Lost contact with the server while checking import status. Reload to check progress.', he: 'החיבור לשרת אבד בעת בדיקת סטטוס הייבוא. רענן כדי לבדוק את ההתקדמות.' },
  ticketImportsUploadHeading: { en: 'Upload tickets', he: 'העלאת כרטיסים' },
  ticketImportsUploadIntroA: {
    en: "Imported tickets are added to the bot's automatic",
    he: 'כרטיסים מיובאים נוספים אוטומטית אל',
  },
  ticketImportsUploadIntroBank: { en: 'Learning Bank', he: 'מאגר הלמידה' },
  ticketImportsUploadIntroB: {
    en: '— the system-managed knowledge store the bot learns from. No bank selection is needed.',
    he: '— מאגר הידע המנוהל על ידי המערכת שממנו הבוט לומד. אין צורך בבחירת מאגר.',
  },
  ticketImportsFilesLabel: {
    en: 'Files (.json / .jsonl, max 200 MB each)',
    he: 'קבצים (.json / .jsonl, עד 200 MB לכל קובץ)',
  },
  ticketImportsDpaConfirm: {
    en: "I confirm operator authority and lawful basis to import these tickets (DPA compliance). Personal data inside the export will be processed under this tenant's data-processing agreement.",
    he: 'אני מאשר סמכות מפעיל ובסיס חוקי לייבוא הכרטיסים הללו (עמידה ב-DPA). מידע אישי שבייצוא יעובד בהתאם להסכם עיבוד הנתונים של ארגון זה.',
  },
  ticketImportsUploading: { en: 'Uploading…', he: 'מעלה…' },
  ticketImportsQueueImport: { en: 'Queue import', he: 'הוסף ייבוא לתור' },
  ticketImportsQueuedMsg: { en: 'Queued {n} import job(s).', he: 'נוספו {n} משימות ייבוא לתור.' },
  ticketImportsUploadFailed: { en: 'Upload failed.', he: 'ההעלאה נכשלה.' },
  ticketImportsRecentHeading: { en: 'Recent imports', he: 'ייבואים אחרונים' },
  ticketImportsSrLatest: {
    en: 'Latest status: {status}, ingested {ingested} of {total}.',
    he: 'סטטוס אחרון: {status}, נקלטו {ingested} מתוך {total}.',
  },
  ticketImportsEmpty: { en: 'No imports yet. Queue one above.', he: 'אין ייבואים עדיין. הוסף אחד למעלה.' },
  ticketImportsColCreated: { en: 'Created', he: 'נוצר' },
  ticketImportsColFormat: { en: 'Format', he: 'פורמט' },
  ticketImportsColStatus: { en: 'Status', he: 'סטטוס' },
  ticketImportsColProgress: { en: 'Progress', he: 'התקדמות' },
  ticketImportsColRequestedBy: { en: 'Requested by', he: 'התבקש על ידי' },
  ticketImportsIngested: { en: '{n} ingested', he: '{n} נקלטו' },
  ticketImportsFailed: { en: ', {n} failed', he: ', {n} נכשלו' },
  ticketImportsSampleToggle: { en: 'Sample preview (no persistence)', he: 'תצוגה מקדימה של דוגמה (ללא שמירה)' },
  ticketImportsSampleHeading: { en: 'Validate a mapping against a sample', he: 'אימות מיפוי מול דוגמה' },
  ticketImportsSampleIntroA: { en: 'Pick one file and click', he: 'בחר קובץ אחד ולחץ' },
  ticketImportsPreviewParse: { en: 'Preview parse', he: 'תצוגה מקדימה של ניתוח' },
  ticketImportsSampleIntroB: {
    en: ". Up to ten tickets are resolved with this tenant's saved mapping and shown below. Nothing is written.",
    he: '. עד עשרה כרטיסים מנותחים באמצעות המיפוי השמור של ארגון זה ומוצגים למטה. דבר אינו נשמר.',
  },
  ticketImportsSampleFileAria: { en: 'Sample file for preview', he: 'קובץ דוגמה לתצוגה מקדימה' },
  ticketImportsParsing: { en: 'Parsing…', he: 'מנתח…' },
  ticketImportsPreviewFailed: { en: 'Preview failed.', he: 'התצוגה המקדימה נכשלה.' },
  ticketImportsNoTickets: { en: 'No tickets resolved from this sample.', he: 'לא נותחו כרטיסים מדוגמה זו.' },
  ticketImportsRoleAgent: { en: 'Agent', he: 'סוכן' },
  ticketImportsRoleCustomer: { en: 'Customer', he: 'לקוח' },
  ticketImportsStatusPending: { en: 'Pending', he: 'ממתין' },
  ticketImportsStatusRunning: { en: 'Running', he: 'רץ' },
  ticketImportsStatusCompleted: { en: 'Completed', he: 'הושלם' },
  ticketImportsStatusFailed: { en: 'Failed', he: 'נכשל' },

  // ═══════════════════════════════════════════════════════════════════════
  //  Ticket import mapping (TicketImportMappingPage)
  // ═══════════════════════════════════════════════════════════════════════
  ticketMappingLoadError: { en: 'Failed to load mapping.', he: 'טעינת המיפוי נכשלה.' },
  ticketMappingLoading: { en: 'Loading mapping…', he: 'טוען מיפוי…' },
  ticketMappingDefaultLoaded: {
    en: 'Default template loaded — click Save to keep them.',
    he: 'תבנית ברירת המחדל נטענה — לחץ שמור כדי לשמור אותה.',
  },
  ticketMappingSaved: { en: 'Mapping saved.', he: 'המיפוי נשמר.' },
  ticketMappingSaveFailed: { en: 'Save failed.', he: 'השמירה נכשלה.' },
  ticketMappingTitle: { en: 'Ticket Import Mapping', he: 'מיפוי ייבוא כרטיסים' },
  ticketMappingSubtitle: {
    en: "Tell us where to find each piece of information in your exported ticket files. Each company has its own settings.",
    he: 'ספר לנו איפה למצוא כל פרט מידע בקובצי הכרטיסים המיוצאים שלך. לכל חברה יש הגדרות משלה.',
  },
  ticketMappingTopFieldsHeading: { en: 'Top-level ticket fields', he: 'שדות כרטיס ברמה העליונה' },
  ticketMappingTicketIdField: { en: 'Ticket id field', he: 'שדה מזהה כרטיס' },
  ticketMappingContactNameField: { en: 'Contact name field', he: 'שדה שם איש קשר' },
  ticketMappingContactEmailField: { en: 'Contact email field', he: 'שדה אימייל איש קשר' },
  ticketMappingSubjectField: { en: 'Subject field', he: 'שדה נושא' },
  ticketMappingCreatedAtField: { en: 'Created-at field', he: 'שדה תאריך יצירה' },
  ticketMappingHistoryArrayPath: { en: 'Location of the conversation history in the file', he: 'מיקום היסטוריית השיחה בקובץ' },
  ticketMappingTurnFieldsHeading: {
    en: 'Per-turn fields (inside the history array)',
    he: 'שדות לכל תור (בתוך מערך ההיסטוריה)',
  },
  ticketMappingTimestampField: { en: 'Timestamp field', he: 'שדה חותמת זמן' },
  ticketMappingSenderField: { en: 'Sender field', he: 'שדה שולח' },
  ticketMappingTextField: { en: 'Text field', he: 'שדה טקסט' },
  ticketMappingRoleHeading: { en: 'Agent-role inference', he: 'הסקת תפקיד סוכן' },
  ticketMappingRoleHintA: { en: 'A turn is treated as an', he: 'תור נחשב לתור' },
  ticketMappingRoleAgentWord: { en: 'agent', he: 'סוכן' },
  ticketMappingRoleHintB: {
    en: "turn when the sender's email domain matches one of these, OR the sender value matches one of these exact strings. Otherwise the turn is treated as a",
    he: 'כאשר דומיין האימייל של השולח תואם לאחד מאלה, או שערך השולח תואם לאחת מהמחרוזות המדויקות הללו. אחרת התור נחשב לתור',
  },
  ticketMappingRoleCustomerWord: { en: 'customer', he: 'לקוח' },
  ticketMappingRoleHintC: { en: 'turn.', he: '.' },
  ticketMappingAgentDomainsLabel: { en: 'Agent email domains', he: 'דומייני אימייל של סוכנים' },
  ticketMappingAgentDomainsPlaceholder: { en: 'e.g. yourcompany.com', he: 'לדוגמה: yourcompany.com' },
  ticketMappingAgentSendersLabel: { en: 'Agent sender values', he: 'ערכי שולח של סוכנים' },
  ticketMappingAgentSendersPlaceholder: { en: 'e.g. agent, staff', he: 'לדוגמה: agent, staff' },
  ticketMappingBotPhrasesLabel: { en: 'Bot phrases', he: 'ביטויי בוט' },
  ticketMappingBotPhrasesHintA: {
    en: 'Turns whose text contains one of these phrases are classified as',
    he: 'תורות שהטקסט שלהם מכיל אחד מהביטויים הללו מסווגים כתורות',
  },
  ticketMappingBotWord: { en: 'bot', he: 'בוט' },
  ticketMappingBotPhrasesHintB: {
    en: 'turns and excluded from learning (both behavior and facts mining). Useful for automated-reply prefixes, ticket-created confirmations, IVR text, and canned greetings.',
    he: 'ומוחרגים מהלמידה (הן כריית התנהגות והן כריית עובדות). שימושי עבור קידומות של תשובות אוטומטיות, אישורי פתיחת כרטיס, טקסט IVR וברכות מוכנות מראש.',
  },
  ticketMappingBotPhrasesPlaceholder: { en: 'e.g. Bot:', he: 'לדוגמה: Bot:' },
  ticketMappingRoleStrategyLabel: { en: 'How to tell agents and customers apart', he: 'איך להבחין בין נציגים ללקוחות' },
  ticketMappingStrategySender: { en: 'By sender label (default)', he: 'לפי תווית השולח (ברירת מחדל)' },
  ticketMappingStrategyLlm: {
    en: "Use AI to guess (for files that don't clearly label who's who)",
    he: 'השתמש ב-AI כדי לנחש (עבור קבצים שלא מציינים בבירור מי הוא מי)',
  },
  ticketMappingLlmCostNote: {
    en: 'This uses AI to guess who is speaking when it isn\'t clear from the file. It may add a small cost for each message it has to guess on.',
    he: 'זה משתמש ב-AI כדי לנחש מי מדבר כאשר זה לא ברור מהקובץ. הדבר עשוי להוסיף עלות קטנה עבור כל הודעה שיש לנחש.',
  },
  ticketMappingResetBtn: { en: 'Reset to default template', he: 'אפס לתבנית ברירת המחדל' },

  // ═══════════════════════════════════════════════════════════════════════
  //  Charts (ConversationVolumeChart, SlaComplianceChart)
  // ═══════════════════════════════════════════════════════════════════════
  chartVolumeTitle: { en: 'Conversation Volume', he: 'נפח שיחות' },
  chartVolumeTotal: { en: 'Total', he: 'סה״כ' },
  chartVolumeAiHandled: { en: 'AI Handled', he: 'טופל ע״י בינה מלאכותית' },
  chartVolumeEscalated: { en: 'Escalated', he: 'הוסלם' },
  chartSlaTitle: { en: 'SLA Compliance', he: 'עמידה ב-SLA' },
  chartSlaTarget: { en: 'Target: {n}%', he: 'יעד: {n}%' },
  chartSlaCompliance: { en: 'Compliance', he: 'עמידה' },
} satisfies Record<string, { en: string; he: string }>;
